/**
 * 도움말 전용 Tiptap 노드 스키마 — **BE 저장 JSON / 읽기 렌더러 / 에디터 3자의 계약**.
 *
 * 노드 이름이나 attr 이름을 바꾸면 이미 저장된 본문이 렌더되지 않는다(서버는 body 를
 * 구조 검증 없이 저장하므로 아무도 막아주지 않는다). 여기가 유일한 출처다.
 *
 * 이름에 `docs` 접두어를 붙인 이유: 게시판(board)이 쓰는 StarterKit 기본 노드와
 * 같은 jsonb 공간을 쓰므로, 이름이 겹치면 게시판 본문이 도움말 블록으로 렌더된다.
 */
export const DOCS_NODE = {
  CALLOUT: 'docsCallout',
  STEPS: 'docsSteps',
  STEP: 'docsStep',
  FIGURE: 'docsFigure',
  SCREENCAST: 'docsScreencast',
  YOUTUBE: 'docsYoutube',
} as const;

export type DocsNodeName = (typeof DOCS_NODE)[keyof typeof DOCS_NODE];

/**
 * Callout 톤. 값이 곧 저장되는 attr 이라 함부로 못 바꾼다.
 * 스타일·라벨은 `components/blocks/blockStyles.ts` 의 `CALLOUT_STYLE` 이 갖는다.
 */
export const CALLOUT_TONE = ['tip', 'warn', 'danger'] as const;
export type CalloutTone = (typeof CALLOUT_TONE)[number];

export interface CalloutAttrs {
  tone: CalloutTone;
  title: string | null;
}

export interface StepAttrs {
  /** 단계 제목. 본문(children)은 설명이다. */
  title: string;
}

export interface FigureAttrs {
  /** CDN 키. `docs/` prefix 는 붙이지 않는다 — docsImageUrl 이 붙인다. */
  src: string;
  alt: string;
  /** 캡처 창 제목 표시줄 문구. 없으면 표시줄을 그리지 않는다. */
  window: string | null;
  caption: string | null;
}

export interface ScreencastAttrs {
  /**
   * 두 형태를 받는다:
   *  - 절대 URL — 에디터에서 올린 영상 파일 하나
   *  - 확장자 없는 CDN 키 — `<src>.webm` / `<src>.mp4` / `<src>.webp`(poster)
   */
  src: string;
  alt: string;
  /** 첫 프레임 이미지(선택). 업로드본에만 쓴다 — 키 방식은 `<src>.webp` 를 자동으로 본다. */
  poster: string | null;
  caption: string | null;
}

export interface YoutubeAttrs {
  /** 영상 ID (`https://youtu.be/<id>` 뒷부분). 재생목록은 넣지 않는다. */
  videoId: string;
  title: string;
  description: string | null;
  duration: string | null;
}

/** 에디터가 새 블록을 꽂을 때 쓰는 기본값. 렌더러의 fallback 과 같아야 한다. */
export const DOCS_NODE_DEFAULTS = {
  callout: { tone: 'tip', title: null } satisfies CalloutAttrs,
  figure: { src: '', alt: '', window: null, caption: null } satisfies FigureAttrs,
  screencast: {
    src: '',
    alt: '',
    poster: null,
    caption: null,
  } satisfies ScreencastAttrs,
  youtube: {
    videoId: '',
    title: '',
    description: null,
    duration: null,
  } satisfies YoutubeAttrs,
} as const;
